import { Component } from '@theme/component';

const STORAGE_KEY = 'cavador-flooring-project:v1';
const MAX_FINISHES = 8;
const MAX_ROOMS = 20;
const MAX_BYTES = 100000;
const SQM_TO_SQFT = 1 / (0.3048 * 0.3048);
const FIELDS = ['model', 'material', 'collection', 'colour', 'finish', 'dimensions', 'thickness', 'wear_layer', 'installation', 'water_resistance', 'underpad', 'warranty', 'coverage_note'];

const cleanText = (value, max = 160) => typeof value === 'string' ? value.trim().slice(0, max) : '';
const number = value => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 10000000 ? value : null;
const positive = value => number(value) > 0 ? value : null;
const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);

/** Only same-store product links and supplier images already hosted by Shopify are retained. */
export function normalizeProduct(input, origin = window.location.origin) {
  if (!input || typeof input !== 'object' || !/^\d+$/.test(String(input.id)) || !/^[a-z0-9-]{1,100}$/.test(input.handle || '')) return null;
  const title = cleanText(input.title);
  let url;
  let image = '';
  try {
    const parsed = new URL(input.url, origin);
    if (parsed.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9-]+\/?$/i.test(parsed.pathname)) return null;
    if (parsed.pathname.split('/').filter(Boolean).at(-1) !== input.handle) return null;
    url = parsed.pathname;
  } catch { return null; }
  if (!title) return null;
  if (input.image) {
    try {
      const parsed = new URL(input.image, origin);
      if (parsed.protocol === 'https:' && (parsed.hostname === 'cdn.shopify.com' || (parsed.origin === origin && parsed.pathname.startsWith('/cdn/shop/')))) image = parsed.href;
    } catch {}
  }
  const values = {};
  for (const field of FIELDS) values[field] = cleanText(input.values?.[field], 600);
  values.coverage = positive(input.coveragePerBox) || positive(input.values?.coverage);
  const planks = positive(input.planksPerBox);
  const price = positive(input.priceCents);
  return { id: String(input.id).slice(0, 30), handle: input.handle, title, url, image, values, coveragePerBox: values.coverage, planksPerBox: Number.isInteger(planks) ? planks : null, priceCents: Number.isInteger(price) ? price : null, currency: /^[A-Z]{3}$/.test(input.currency || '') ? input.currency : '' };
}

/** Stored quantities are discarded; totals are always derived from this finish's verified coverage. */
export function normalizeCalculation(input, product) {
  if (!input || input.version !== 1 || input.handle !== product.handle || !['area', 'dims', 'rooms'].includes(input.mode) || !['imperial', 'metric'].includes(input.units) || !Array.isArray(input.rooms) || !input.rooms.length || input.rooms.length > MAX_ROOMS) return null;
  if (input.mode !== 'rooms' && (input.rooms.length !== 1 || input.rooms[0]?.mode !== input.mode)) return null;
  const waste = number(input.waste);
  if (![0, 5, 10, 15, 20].includes(waste)) return null;
  const rooms = [];
  for (const room of input.rooms) {
    if (!room || !['area', 'dims'].includes(room.mode)) return null;
    const normalized = { name: cleanText(room.name, 60), mode: room.mode, area: number(room.area), length: number(room.length), width: number(room.width) };
    rooms.push(normalized);
  }
  let areaSqft = 0;
  let measurementsComplete = true;
  for (const room of rooms) {
    const area = room.mode === 'dims' ? (room.length || 0) * (room.width || 0) : (room.area || 0);
    if (!(area > 0) || !Number.isFinite(area) || area > 100000000) measurementsComplete = false;
    else areaSqft += area * (input.units === 'metric' ? SQM_TO_SQFT : 1);
  }
  const coveragePerBox = product.values.coverage;
  const requiredSqft = areaSqft * (1 + waste / 100);
  const ratio = coveragePerBox ? requiredSqft / coveragePerBox : null;
  const valid = measurementsComplete && areaSqft > 0 && coveragePerBox > 0 && Number.isFinite(ratio) && ratio <= 10000000;
  const tolerance = ratio ? Number.EPSILON * 8 * Math.max(1, Math.abs(ratio)) : 0;
  const boxes = valid ? Math.max(1, Math.ceil(ratio - tolerance)) : null;
  return { version: 1, handle: product.handle, mode: input.mode, units: input.units, waste, rooms, areaSqft, requiredSqft, boxes, coveragePerBox, planksPerBox: product.planksPerBox, totalCoverageSqft: valid ? boxes * coveragePerBox : null, priceCents: product.priceCents, measurementsComplete, valid };
}

export function normalizeProject(input, defaultName = '', origin = window.location.origin) {
  const project = { version: 1, name: cleanText(defaultName, 80), updatedAt: '', finishes: [] };
  if (!input || input.version !== 1 || !Array.isArray(input.finishes) || input.finishes.length > MAX_FINISHES) return project;
  project.name = cleanText(input.name, 80) || project.name;
  project.updatedAt = typeof input.updatedAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(input.updatedAt) && Number.isFinite(Date.parse(input.updatedAt)) ? input.updatedAt.slice(0, 30) : '';
  const seen = new Set();
  for (const finish of input.finishes) {
    const product = normalizeProduct(finish?.product, origin);
    if (!product || seen.has(product.handle)) continue;
    seen.add(product.handle);
    const savedAt = typeof finish.savedAt === 'string' && Number.isFinite(Date.parse(finish.savedAt)) ? finish.savedAt.slice(0, 30) : project.updatedAt;
    project.finishes.push({ product, calculation: normalizeCalculation(finish.calculation, product), savedAt });
  }
  return project;
}

export function persistProject(storage, project) {
  const serialized = JSON.stringify(project);
  if (serialized.length > MAX_BYTES) return false;
  try { storage.setItem(STORAGE_KEY, serialized); return true; } catch { return false; }
}

const formatNumber = (value, locale) => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
const formatMoney = (value, currency, locale) => {
  if (!(value > 0) || !currency) return '';
  try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value / 100); } catch { return ''; }
};
const roomName = (room, index, labels) => room.name || labels.room.replace('__NUMBER__', String(index + 1));
const roomValue = (room, units, labels, locale) => room.mode === 'dims'
  ? `${room.length === null ? '—' : formatNumber(room.length, locale)} × ${room.width === null ? '—' : formatNumber(room.width, locale)} ${units === 'metric' ? labels.m : labels.ft}`
  : `${room.area === null ? '—' : formatNumber(room.area, locale)} ${units === 'metric' ? labels.sqm : labels.sqft}`;

/** All merchant and shopper content is escaped; the downloaded brief contains no executable script. */
export function buildProjectBrief(project, labels, { origin = window.location.origin, locale = 'en' } = {}) {
  const safe = normalizeProject(project, labels.default_name, origin);
  const e = escapeHTML;
  const n = value => formatNumber(value, locale);
  const definition = (label, value) => `<div><dt>${e(label)}</dt><dd>${e(value)}</dd></div>`;
  const sections = safe.finishes.map(({ product, calculation, savedAt }, index) => {
    const details = FIELDS.map(field => definition(labels[field], product.values[field] || labels.not_confirmed)).join('');
    const price = formatMoney(product.priceCents, product.currency, locale) || labels.price_pending;
    const coverage = product.values.coverage ? `${n(product.values.coverage)} ${labels.sqft}` : labels.not_confirmed;
    let estimate = `<p class="pending">${e(labels.measurements_pending)}</p>`;
    if (calculation) {
      const measured = calculation.measurementsComplete;
      const rooms = calculation.rooms.map((room, i) => `<tr><th scope="row">${e(roomName(room, i, labels))}</th><td>${e(roomValue(room, calculation.units, labels, locale))}</td></tr>`).join('');
      const totals = definition(labels.waste, `${n(calculation.waste)}%`) +
        (measured ? definition(labels.area, `${n(calculation.areaSqft)} ${labels.sqft}`) + definition(labels.required, `${n(calculation.requiredSqft)} ${labels.sqft}`) : '') +
        (calculation.valid ? definition(labels.boxes, n(calculation.boxes)) + definition(labels.total_coverage, `${n(calculation.totalCoverageSqft)} ${labels.sqft}`) + definition(labels.estimated_cost, formatMoney(calculation.boxes * product.priceCents, product.currency, locale) || labels.price_pending) : '');
      const pending = !measured ? labels.incomplete : !product.values.coverage ? labels.coverage_pending : '';
      estimate = `<h3>${e(labels.rooms)}</h3><table>${rooms}</table><dl>${totals}</dl>${pending ? `<p class="pending">${e(pending)}</p>` : ''}`;
    }
    const savedDate = savedAt ? new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'America/Toronto' }).format(new Date(savedAt)) : '';
    const referenceDate = savedDate ? `<p class="reference">${e(labels.saved_reference.replace('__DATE__', savedDate))}</p>` : '';
    return `<section class="finish"><header>${product.image ? `<img src="${e(product.image)}" alt="${e(labels.brief_supplier_image)}" width="112" height="112">` : ''}<div><p class="number">${index + 1}</p><h2>${e(product.title)}</h2><a href="${e(new URL(product.url, origin).href)}">${e(labels.brief_source)}</a>${referenceDate}</div></header><dl class="facts">${details}${definition(labels.box_coverage, coverage)}${definition(labels.planks, product.planksPerBox || labels.not_confirmed)}${definition(labels.price, price)}</dl>${estimate}</section>`;
  }).join('');
  const date = safe.updatedAt ? new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'America/Toronto' }).format(new Date(safe.updatedAt)) : '';
  return `<!doctype html><html lang="${e(locale)}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="referrer" content="no-referrer"><title>${e(safe.name)} · ${e(labels.brief_title)}</title><style>body{max-width:850px;margin:40px auto;padding:0 24px;color:#202020;background:#fff;font:14px/1.6 Arial,sans-serif}h1{margin:4px 0 12px;font:normal 36px/1.2 Georgia,serif}h2{margin:2px 0 8px;font:normal 24px/1.3 Georgia,serif}h3{margin:24px 0 8px;font-size:14px}a{color:#64232e;overflow-wrap:anywhere}.eyebrow,.number{color:#676767;font-size:11px;letter-spacing:.12em;text-transform:uppercase}.note{padding:16px 0;border-block:1px solid #dedede;color:#555}.finish{margin-top:32px;padding-top:24px;border-top:1px solid #dedede}.finish header{display:flex;gap:20px;align-items:center}.finish img{object-fit:cover}.number{margin:0}dl{margin:20px 0}dl>div{display:grid;grid-template-columns:180px minmax(0,1fr);gap:12px;padding:5px 0;border-bottom:1px solid #eee}dt{color:#676767}dd{margin:0;overflow-wrap:anywhere}table{width:100%;border-collapse:collapse}th,td{text-align:left;font-weight:normal;padding:8px 0;border-bottom:1px solid #eee}td{text-align:right}.pending{color:#676767}footer{margin-top:36px;color:#676767;font-size:12px}@media(max-width:500px){body{margin:24px auto;padding:0 18px}dl>div{grid-template-columns:minmax(0,1fr) minmax(0,1.3fr)}.finish header{align-items:flex-start}.finish img{width:72px;height:72px}h1{font-size:30px}h2{font-size:21px}}@media print{body{max-width:none;margin:0;padding:0;font-size:11px}.finish{break-inside:avoid}h1{font-size:28px}h2{font-size:20px}a{color:#202020}a[href]::after{content:' (' attr(href) ')';font-size:9px}header img{width:80px;height:80px}footer{font-size:10px}}</style></head><body><p class="eyebrow">CAVADOR · ${e(labels.brief_title)}</p><h1>${e(safe.name)}</h1>${date ? `<p>${e(labels.brief_date)}: ${e(date)}</p>` : ''}<p class="note">${e(labels.installer_notice)}</p>${sections}<footer>${e(labels.brief_reference_notice)}</footer></body></html>`;
}

/** @extends {Component<{dialog: HTMLDialogElement, openButton: HTMLButtonElement, closeButton: HTMLButtonElement, nameInput: HTMLInputElement, list: HTMLDivElement, current: HTMLDivElement, count: HTMLSpanElement, empty: HTMLParagraphElement, saveButton: HTMLButtonElement, addComparedButton: HTMLButtonElement, downloadButton: HTMLButtonElement, storageNotice: HTMLParagraphElement, status: HTMLParagraphElement}>} */
class CavadorFlooringProjects extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.statusTimer);
    if (this.refs.dialog?.open) this.refs.dialog.close();
    for (const url of this.downloadURLs || []) URL.revokeObjectURL(url);
    this.downloadURLs = [];
    super.disconnectedCallback();
  }

  initialize() {
    this.controller?.abort();
    clearTimeout(this.statusTimer);
    try {
      this.labels = JSON.parse(this.querySelector('[data-project-labels]')?.textContent || '{}');
      const record = JSON.parse(this.querySelector('[data-project-product] [data-floor-compare-product]')?.textContent || 'null');
      if (record) {
        record.priceCents = positive(Number(this.dataset.priceCents));
        record.currency = this.dataset.currency;
        const facts = this.querySelector('[data-project-product] [data-floor-compare-facts]');
        for (const row of facts?.content?.querySelectorAll('dl > div') || []) {
          const label = row.querySelector('dt')?.textContent.trim();
          for (const field of ['water_resistance', 'underpad']) {
            if (label === this.labels['fact_' + field]) {
              record.values ||= {};
              record.values[field] = cleanText(row.querySelector('dd')?.textContent, 600);
            }
          }
        }
      }
      this.product = normalizeProduct(record);
    } catch { return; }
    if (!this.product || !this.refs.dialog || typeof this.refs.dialog.showModal !== 'function' || !this.labels.default_name) return;
    this.locale = this.dataset.locale || 'en';
    this.controller = new AbortController();
    this.downloadURLs ||= [];
    this.project ||= this.readSavedProject();
    const savedCurrent = this.project.finishes.find(entry => entry.product.handle === this.product.handle);
    if (savedCurrent) {
      savedCurrent.product = this.product;
      savedCurrent.calculation = normalizeCalculation(savedCurrent.calculation, this.product);
      savedCurrent.savedAt = new Date().toISOString();
    }
    this.refs.nameInput.value = this.project.name;
    this.refs.openButton.hidden = false;
    const options = { signal: this.controller.signal };
    this.refs.nameInput.addEventListener('change', () => this.saveName(), options);
    this.refs.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeProject(); }, options);
    this.refs.dialog.addEventListener('close', () => this.returnFocus?.isConnected && this.returnFocus.focus({ preventScroll: true }), options);
    this.refs.dialog.addEventListener('click', event => {
      if (event.target !== this.refs.dialog) return;
      const rect = this.refs.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeProject();
    }, options);
    this.refs.list.addEventListener('click', event => {
      const button = event.target instanceof Element ? event.target.closest('button[data-project-action]') : null;
      if (!button) return;
      if (button.dataset.projectAction === 'remove') this.removeFinish(button.dataset.handle);
      else if (button.dataset.projectAction === 'load') this.loadMeasurements(button.dataset.handle);
    }, options);
    document.addEventListener('cavador:flooring-calculation-change', event => {
      if (event.detail?.handle !== this.product.handle) return;
      this.renderCurrent();
    }, options);
    document.addEventListener('cavador:comparison-change', () => this.render(), options);
    window.addEventListener('storage', event => {
      if (event.key !== STORAGE_KEY) return;
      this.project = this.readSavedProject();
      this.refs.nameInput.value = this.project.name;
      this.render();
    }, options);
    this.render();
  }

  readSavedProject() {
    let raw;
    try { raw = localStorage.getItem(STORAGE_KEY); } catch {
      this.storageMessage = this.labels.storage_unavailable;
      return normalizeProject(null, this.labels.default_name);
    }
    try {
      if (!raw) return normalizeProject(null, this.labels.default_name);
      if (raw.length > MAX_BYTES) throw new Error('oversize');
      const parsed = JSON.parse(raw);
      if (parsed?.version !== 1 || !Array.isArray(parsed.finishes) || parsed.finishes.length > MAX_FINISHES) throw new Error('invalid');
      return normalizeProject(parsed, this.labels.default_name);
    } catch {
      this.storageMessage = this.labels.storage_invalid;
      return normalizeProject(null, this.labels.default_name);
    }
  }

  calculator() {
    return [...document.querySelectorAll('.aurora-calc, [data-flooring-calculator]')].find(root => (root.dataset.flooringHandle || root.dataset.handle) === this.product.handle);
  }

  currentCalculation() {
    return normalizeCalculation(this.calculator()?.cavadorFlooringCalculation, this.product);
  }

  comparedProducts() {
    try {
      const selected = window.CavadorFlooringComparison?.getSelected?.();
      return Array.isArray(selected) ? selected.slice(0, 3).map(product => normalizeProduct({ ...product, currency: product.currency || this.product.currency })).filter(Boolean) : [];
    } catch { return []; }
  }

  /** Opens a native modal, which supplies focus containment and keyboard Escape support. */
  openProject() {
    this.returnFocus = document.activeElement;
    this.render();
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.refs.closeButton.focus({ preventScroll: true });
  }

  closeProject() {
    if (this.refs.dialog?.open) this.refs.dialog.close();
  }

  saveName(persist = true) {
    this.project.name = cleanText(this.refs.nameInput.value, 80) || this.labels.default_name;
    this.refs.nameInput.value = this.project.name;
    if (persist && this.project.finishes.length) this.persist();
  }

  persist(message = this.labels.saved) {
    this.project.updatedAt = new Date().toISOString();
    let saved = false;
    try { saved = persistProject(localStorage, this.project); } catch {}
    if (!saved) this.storageMessage = this.labels.storage_unavailable;
    else this.storageMessage = '';
    this.render();
    this.announce(saved ? message : this.labels.storage_unavailable);
  }

  saveCurrent() {
    if (!this.project.finishes.some(entry => entry.product.handle === this.product.handle) && this.project.finishes.length >= MAX_FINISHES) { this.announce(this.labels.limit); return; }
    this.saveName(false);
    const entry = { product: this.product, calculation: this.currentCalculation(), savedAt: new Date().toISOString() };
    const index = this.project.finishes.findIndex(finish => finish.product.handle === this.product.handle);
    if (index < 0) this.project.finishes.push(entry);
    else this.project.finishes[index] = entry;
    this.persist();
  }

  addCompared() {
    const products = this.comparedProducts();
    const additions = products.filter(product => !this.project.finishes.some(entry => entry.product.handle === product.handle));
    if (this.project.finishes.length + additions.length > MAX_FINISHES) { this.announce(this.labels.limit); return; }
    if (!additions.length) return;
    this.saveName(false);
    for (const product of additions) this.project.finishes.push({ product: product.handle === this.product.handle ? this.product : product, calculation: product.handle === this.product.handle ? this.currentCalculation() : null, savedAt: new Date().toISOString() });
    this.persist(this.labels.compared_added);
  }

  removeFinish(handle) {
    const index = this.project.finishes.findIndex(entry => entry.product.handle === handle);
    if (index < 0) return;
    this.project.finishes.splice(index, 1);
    this.persist(this.labels.removed);
    const focus = this.refs.list.querySelector('button') || this.refs.saveButton;
    focus.focus({ preventScroll: true });
  }

  loadMeasurements(handle) {
    if (handle !== this.product.handle) return;
    const entry = this.project.finishes.find(finish => finish.product.handle === handle);
    const calculator = this.calculator();
    if (!entry?.calculation || typeof calculator?.restoreCavadorFlooringCalculation !== 'function') { this.announce(this.labels.measurement_unavailable); return; }
    const state = normalizeCalculation(entry.calculation, this.product);
    if (!state || calculator.restoreCavadorFlooringCalculation(state) === false) { this.announce(this.labels.measurement_unavailable); return; }
    this.renderCurrent();
    this.announce(this.labels.measurements_restored);
  }

  downloadBrief() {
    if (!this.project.finishes.length) return;
    this.saveName();
    const html = buildProjectBrief(this.project, this.labels, { locale: this.locale });
    let url;
    try {
      url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = (this.project.name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'cavador-flooring-project') + '.html';
      this.refs.dialog.append(link);
      link.click();
      link.remove();
      this.downloadURLs.push(url);
      // Keep the latest download alive until disconnect; retire older URLs to bound memory.
      if (this.downloadURLs.length > 2) URL.revokeObjectURL(this.downloadURLs.shift());
      this.announce(this.labels.downloaded);
    } catch {
      if (url) URL.revokeObjectURL(url);
      this.announce(this.labels.download_error);
    }
  }

  announce(message) {
    clearTimeout(this.statusTimer);
    this.refs.status.textContent = '';
    this.statusTimer = setTimeout(() => { if (this.isConnected) this.refs.status.textContent = message; }, 20);
  }

  summary(product, calculation) {
    if (!calculation) return this.labels.measurements_pending;
    if (!calculation.measurementsComplete) return this.labels.incomplete;
    if (!calculation.valid) return this.labels.coverage_pending;
    const n = value => formatNumber(value, this.locale);
    const estimate = formatMoney(calculation.boxes * product.priceCents, product.currency, this.locale) || this.labels.price_pending;
    return `${this.labels.area}: ${n(calculation.areaSqft)} ${this.labels.sqft} · ${this.labels.waste}: ${n(calculation.waste)}% · ${this.labels.boxes}: ${n(calculation.boxes)} · ${estimate}`;
  }

  finishDOM(product, calculation) {
    const finish = document.createElement('div');
    finish.className = 'cfp__finish';
    if (product.image) {
      const image = document.createElement('img');
      image.className = 'cfp__image';
      image.src = product.image;
      image.alt = '';
      image.width = 64;
      image.height = 64;
      image.loading = 'lazy';
      finish.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'cfp__image';
      placeholder.setAttribute('aria-hidden', 'true');
      finish.append(placeholder);
    }
    const body = document.createElement('div');
    const title = document.createElement('a');
    title.className = 'cfp__finish-name';
    title.href = product.url;
    title.textContent = product.title;
    body.append(title);
    if (product.values.model) {
      const model = document.createElement('p');
      model.className = 'cfp__model';
      model.textContent = product.values.model;
      body.append(model);
    }
    const summary = document.createElement('p');
    summary.className = 'cfp__summary';
    summary.textContent = this.summary(product, calculation);
    body.append(summary);
    finish.append(body);
    return finish;
  }

  renderCurrent() {
    this.refs.current.replaceChildren(this.finishDOM(this.product, this.currentCalculation()));
  }

  renderComparedButton() {
    this.refs.addComparedButton.hidden = !this.comparedProducts().some(product => !this.project.finishes.some(entry => entry.product.handle === product.handle));
  }

  render() {
    for (const entry of this.project.finishes) {
      let latest = null;
      try { latest = window.CavadorFlooringComparison?.getProduct?.(entry.product.handle); } catch {}
      const product = entry.product.handle === this.product.handle ? this.product : latest ? normalizeProduct({ ...latest, currency: latest.currency || this.product.currency }) : null;
      if (product && JSON.stringify(product) !== JSON.stringify(entry.product)) {
        entry.product = product;
        entry.calculation = normalizeCalculation(entry.calculation, product);
        entry.savedAt = new Date().toISOString();
      }
    }
    this.renderCurrent();
    this.renderComparedButton();
    this.refs.empty.hidden = !!this.project.finishes.length;
    this.refs.count.hidden = !this.project.finishes.length;
    this.refs.count.textContent = `(${this.project.finishes.length})`;
    this.refs.downloadButton.disabled = !this.project.finishes.length;
    this.refs.saveButton.textContent = this.project.finishes.some(entry => entry.product.handle === this.product.handle) ? this.labels.update_current : this.labels.save_current;
    this.refs.storageNotice.textContent = this.storageMessage || this.labels.browser_notice;
    const items = this.project.finishes.map(entry => {
      // Refresh this finish's verified facts when revisiting its page, and derive estimates again.
      const product = entry.product.handle === this.product.handle ? this.product : entry.product;
      const calculation = normalizeCalculation(entry.calculation, product);
      const item = document.createElement('article');
      item.className = 'cfp__item';
      item.append(this.finishDOM(product, calculation));
      if (calculation) {
        const details = document.createElement('details');
        details.className = 'cfp__rooms';
        const summary = document.createElement('summary');
        summary.textContent = this.labels.rooms;
        const list = document.createElement('dl');
        for (const [i, room] of calculation.rooms.entries()) {
          const row = document.createElement('div');
          const name = document.createElement('dt');
          const value = document.createElement('dd');
          name.textContent = roomName(room, i, this.labels);
          value.textContent = roomValue(room, calculation.units, this.labels, this.locale);
          row.append(name, value);
          list.append(row);
        }
        details.append(summary, list);
        item.append(details);
      }
      const actions = document.createElement('div');
      actions.className = 'cfp__actions';
      if (product.handle === this.product.handle && calculation) {
        const load = document.createElement('button');
        load.type = 'button';
        load.className = 'cfp__text-button';
        load.dataset.projectAction = 'load';
        load.dataset.handle = product.handle;
        load.textContent = this.labels.load;
        actions.append(load);
      } else {
        const view = document.createElement('a');
        view.href = product.url;
        view.className = 'cfp__link';
        view.textContent = this.labels.view;
        actions.append(view);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cfp__text-button';
      remove.dataset.projectAction = 'remove';
      remove.dataset.handle = product.handle;
      remove.textContent = this.labels.remove;
      remove.setAttribute('aria-label', this.labels.remove_label.replace('__TITLE__', product.title));
      actions.append(remove);
      item.append(actions);
      return item;
    });
    this.refs.list.replaceChildren(...items);
  }
}

if (!customElements.get('cavador-flooring-projects')) customElements.define('cavador-flooring-projects', CavadorFlooringProjects);
